import React from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "../ui/Badge";
import { ChangelogMarkdown } from "../ChangelogMarkdown";
import type { PartialEnvelope, StreamPhase, StreamUsage } from "../../hooks/useAdminStream";

const PHASE_LABEL: Record<Exclude<StreamPhase, null>, string> = {
  commits: "Buscando commits",
  ai: "Gerando com IA",
  saving: "Salvando",
};

const SECTION_LABEL: Record<string, string> = {
  novidades: "Novidades",
  melhorias: "Melhorias",
  correcoes: "Correções",
  seguranca: "Segurança",
};

export interface GenerationProgressProps {
  isRunning: boolean;
  tag: string | null;
  phase: StreamPhase;
  provider: string | null;
  envelope: PartialEnvelope | null;
  markdown: string;
  usage: StreamUsage | null;
  error: string | null;
  errorDetail: string | null;
  notices?: string[];
  completed?: string[];
  cost?: number | null;
  showContent?: boolean;
}

const EnvelopePreview: React.FC<{ envelope: PartialEnvelope }> = ({
  envelope,
}) => (
  <div className="space-y-4">
    {envelope.title && (
      <h3 className="text-lg font-semibold text-foreground">{envelope.title}</h3>
    )}
    {envelope.description && (
      <p className="text-sm text-muted-foreground leading-relaxed">
        {envelope.description}
      </p>
    )}
    {(envelope.sections ?? []).map((section, index) => (
      <div key={section?.kind ?? index} className="space-y-1.5">
        <p className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
          {SECTION_LABEL[section?.kind ?? ""] ?? section?.kind}
        </p>
        <ul className="space-y-1 text-sm text-foreground list-disc pl-5">
          {(section?.items ?? []).map((item, itemIndex) => (
            <li key={itemIndex}>{item}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export const GenerationProgress: React.FC<GenerationProgressProps> = ({
  isRunning,
  tag,
  phase,
  provider,
  envelope,
  markdown,
  usage,
  error,
  errorDetail,
  notices = [],
  completed = [],
  cost,
  showContent = false,
}) => {
  const hasAnything =
    isRunning || markdown || envelope || error || notices.length > 0;
  if (!hasAnything) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {isRunning && (
          <Loader2
            className="w-3.5 h-3.5 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        )}
        {tag && <Badge tone="neutral">{tag}</Badge>}
        {phase && <Badge tone="info">{PHASE_LABEL[phase]}</Badge>}
        {provider && (
          <span className="text-xs text-muted-foreground">
            via <span className="text-foreground font-medium">{provider}</span>
          </span>
        )}
      </div>

      {error && (
        <div className="border border-destructive/20 bg-destructive/5 rounded-md p-4 space-y-2">
          <p className="text-sm text-destructive">{error}</p>
          {errorDetail && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">
                Resposta crua do provider
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-words font-mono">
                {errorDetail}
              </pre>
            </details>
          )}
        </div>
      )}

      {notices.map((notice, index) => (
        <p
          key={index}
          className="text-sm text-muted-foreground border border-border rounded-md px-4 py-3"
        >
          {notice}
        </p>
      ))}

      {completed.length > 1 && (
        <p className="text-xs text-muted-foreground">
          Concluídas: {completed.join(", ")}
        </p>
      )}

      {showContent &&
        (markdown ? (
          <div className="px-4 py-4 bg-background border border-input rounded-md">
            <ChangelogMarkdown>{markdown}</ChangelogMarkdown>
          </div>
        ) : (
          envelope && (
            <div className="px-4 py-4 bg-background border border-input rounded-md">
              <EnvelopePreview envelope={envelope} />
            </div>
          )
        ))}

      {usage && (
        <p className="text-xs text-muted-foreground">
          Entrada {usage.inputTokens ?? "?"} tokens, saída{" "}
          {usage.outputTokens ?? "?"} tokens
          {typeof cost === "number" && `, custo estimado US$ ${cost.toFixed(4)}`}
        </p>
      )}
    </div>
  );
};
